import { createApi } from "@reduxjs/toolkit/dist/query/react";

import baseQuery from "../baseQuery";
import { apiConstants } from "../../common/constants";
import { DeliveryPerformance } from "./DeliveryPerformance";
import { TATPerformance } from "./TATPerformance";
import { RVPPickupPerformance } from "./RVPPickupPerformance";

async function getDeliveryPerformanceFn(queryParams, _api) {
    try {

        const deliveryPerformanceInstance = new DeliveryPerformance(queryParams, _api);
        const tatPerformanceInstance = new TATPerformance(queryParams, _api);
        const rvpPickupPerformanceInstance = new RVPPickupPerformance(queryParams, _api);

        return {
            data: {
                deliveryPerformance: deliveryPerformanceInstance.getPerformace(),
                tatPerformance: tatPerformanceInstance.getPerformace(),
                rvpPickupPerformance: rvpPickupPerformanceInstance.getPerformace(),
                deliveryPerformanceTrend: deliveryPerformanceInstance.getPerformaceTrend(),
                tatPerformanceTrend: tatPerformanceInstance.getPerformaceTrend(),
                rvpPickupPerformanceTrend: rvpPickupPerformanceInstance.getPerformaceTrend(),
            },
        }
    } catch (error) {
        console.error("Error in getDeliveryPerformanceFn", error)
        return { error: "Error in getDeliveryPerformanceFn" }
    }
}

/* All cached courier lists across slices that carry serviceTypePrimary — we need
   that field to map each metric record's courierId → its service type. */
function getCourierListMap(_api) {
    const state = _api.getState()
    const queries = state.courierPerformanceApi?.queries || {}
    const commonQueries = state.commonApiCalls?.queries || {}

    const candidates = []
    Object.keys(queries).forEach((key) => {
        if (key.includes('getAllCouriers')) candidates.push(queries[key]?.data || [])
    })
    Object.keys(commonQueries).forEach((key) => {
        if (key.includes('getAllCourierPartnersCached')) candidates.push(commonQueries[key]?.data || [])
    })

    // prefer a list that actually exposes serviceTypePrimary (includes vary per caller)
    const withServiceType = candidates.find(
        (list) => Array.isArray(list) && list.some((c) => c && (c.serviceTypePrimary || c.service_type_primary))
    )
    const courierList = withServiceType || candidates.find((list) => Array.isArray(list) && list.length) || []
    return courierList.filter((courier) => !courier.isExternal)
}

/* Map a metric record's courierId to its primary service type (the new
   benchmarking key). Falls back to UNKNOWN if the courier isn't found. */
function getServiceType(courierId, courierList) {
    const courier = courierList.find((c) => c.id === courierId)
    return (courier && (courier.serviceTypePrimary || courier.service_type_primary)) || 'UNKNOWN'
}

export const courierPerformanceApi = createApi({
    baseQuery: async (args, api) => {
        const courierList = getCourierListMap(api)
        let { data } = await baseQuery(args)
        if (Array.isArray(data)) {
            data.forEach((item) => {
                // Benchmark by SERVICE TYPE, not courier: re-key courierId to the
                // courier's primary service type so all downstream grouping/lookups
                // (which key on courierId) aggregate by service type instead.
                item.serviceType = getServiceType(item.courierId, courierList)
                item.courierId = item.serviceType
            })
        }

        return { data }
    },
    reducerPath: 'courierPerformanceApi',
    endpoints: (builder) => ({
        getDeliveryMetrics: builder.query({
            query: (data) => ({
                url: apiConstants.DASHBOARD_CONTROLTOWER_GET_DELIVERY_METRICS,
                data,
                type: 'POST',
            }),
        }),
        getTATMetrics: builder.query({
            query: (data) => ({
                url: apiConstants.DASHBOARD_CONTROLTOWER_GET_TAT_METRICS,
                data,
                type: 'POST',
            }),
        }),
        getRVPPickupMetrics: builder.query({
            query: (data) => ({
                url: apiConstants.DASHBOARD_CONTROLTOWER_GET_PICKUP_METRICS,
                data,
                type: 'POST',
            }),
        }),
        getCourierPerformance: builder.query({
            queryFn: getDeliveryPerformanceFn,
        }),
    })
})
